import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api, { errorMessage } from '../api';
import { useAuth } from '../context/AuthContext';
import AuthLayout from '../layout/AuthLayout';
import { Button, Field, Input } from '../components/ui';
import useSeo from '../lib/seo';

export default function Signup() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [ref, setRef] = useState((params.get('ref') || '').toUpperCase());
  const [refInfo, setRefInfo] = useState(null);
  useEffect(() => {
    if (!ref || ref.length < 5) { setRefInfo(null); return undefined; }
    const t = setTimeout(() => api.get(`/api/referrals/check/${encodeURIComponent(ref)}`).then(r => setRefInfo(r.data)).catch(() => setRefInfo({ valid: false })), 300);
    return () => clearTimeout(t);
  }, [ref]);
  useSeo({ title: 'Create your workspace', description: 'Start a free 14-day trial of Merge, grant-writing software for writers and teams. No credit card required.', path: '/signup' });
  const [form, setForm] = useState({ companyName: '', name: '', email: '', password: '', kind: 'writer' });
  const [website, setWebsite] = useState(''); // honeypot: humans never see this field
  const openedAt = React.useRef(Date.now());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/auth/signup', { ...form, website, t: Date.now() - openedAt.current, ref: refInfo?.valid ? ref : undefined });
      signIn(res.data.token, res.data.user);
      navigate('/app/welcome', { replace: true });
    } catch (err) {
      setError(errorMessage(err, 'Could not create your workspace.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Create your workspace" lead={form.kind === 'writer' ? 'Starts with a free 14-day Premium trial.' : "You'll be the admin. Starts with a free 14-day Small Teams trial."} footer={<>Already have an account? <Link to="/login">Sign in</Link></>}
      side={{ title: 'Set up in two minutes.', text: 'Name your workspace, add your first project, and invite the people who help you write.' }}>
      <form onSubmit={submit} noValidate>
        {error && <div className="form-error">{error}</div>}
        <Field label="How will you use Merge?">
          <div className="kind-picker">
            <button type="button" className={`kind-option ${form.kind === 'writer' ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, kind: 'writer' }))}><strong>I'm a grant writer</strong><span>Solo professional or consultant. Write, send for review, download.</span></button>
            <button type="button" className={`kind-option ${form.kind === 'team' ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, kind: 'team' }))}><strong>I'm writing for my organization</strong><span>Solo or with a team. Assign questions, approvals, shared library.</span></button>
          </div>
        </Field>
        <Field label={form.kind === 'writer' ? 'Workspace name' : 'Organization or workspace name'} htmlFor="companyName" hint={form.kind === 'writer' ? 'Your name or business name works fine.' : 'Your team will see this name.'}>
          <Input id="companyName" value={form.companyName} onChange={set('companyName')} placeholder="Riverside Community Foundation" required autoFocus />
        </Field>
        <Field label="Your name" htmlFor="name">
          <Input id="name" value={form.name} onChange={set('name')} autoComplete="name" required />
        </Field>
        <Field label="Work email" htmlFor="email">
          <Input id="email" type="email" value={form.email} onChange={set('email')} autoComplete="email" required />
        </Field>
        <div className="hp" aria-hidden="true"><label htmlFor="website">Website</label><input id="website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={e => setWebsite(e.target.value)} /></div>
        <Field label="Password" htmlFor="password" hint="At least 8 characters.">
          <Input id="password" type="password" value={form.password} onChange={set('password')} autoComplete="new-password" required />
        </Field>
        <Field label="Referral code" htmlFor="ref" hint={refInfo?.valid ? `From ${refInfo.from}. You'll get ${refInfo.discount}.` : ref && refInfo && !refInfo.valid ? 'That code is not valid.' : 'Optional. 10% off your first 3 months.'} error={ref && refInfo && !refInfo.valid ? ' ' : ''}>
          <Input id="ref" value={ref} onChange={e => setRef(e.target.value.toUpperCase())} placeholder="e.g. RIVERS-7K2Q" />
        </Field>
        <Button type="submit" block size="lg" loading={loading}>Create workspace</Button>
        <p className="tiny faint mt-2" style={{ textAlign: 'center' }}>By continuing you agree to the <Link to="/terms">terms</Link> and <Link to="/privacy">privacy policy</Link>.</p>
      </form>
    </AuthLayout>
  );
}
